/**
 * API d'authentification profil Professionnel.
 * Toutes les routes sous /api/auth/professionnel
 */

import { apiFetch, getStoredToken, setStoredToken } from "./client";

const BASE = "/auth/professionnel";

export type ProfessionalUser = {
  id?: string;
  nip?: string;
  email?: string;
  type?: string;
  statut?: string;
  nom?: string;
  prenom?: string;
  specialite?: string;
  profile?: Record<string, unknown>;
  [key: string]: unknown;
};

export type ProfileResponse = ProfessionalUser;

export type LoginResponse = {
  token?: string;
  accessToken?: string;
  user?: ProfessionalUser;
};

type LoginApiBody = LoginResponse | { success?: boolean; message?: string; data?: LoginResponse };

export type LoginResult =
  | { ok: true; data: { requiresOtp: true; email?: string } }
  | { ok: false; message: string };

export type VerifyOtpResult =
  | { ok: true; data?: { token: string; user: ProfessionalUser } }
  | { ok: false; message: string };

export type SignupBody = {
  email: string;
  password: string;
  confirmPassword: string;
  profileData: Record<string, unknown>;
};

export type SignupInfoResponse = {
  signupAllowed?: boolean;
  requirements?: string[];
  actorType?: string;
  [key: string]: unknown;
};

export type ConfirmPasswordResetBody = {
  otpCode: string;
  newPassword: string;
};

export async function login(email: string, password: string): Promise<LoginResult> {
  const out = await apiFetch<LoginApiBody>(BASE + "/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  // Flux validé avec OTP obligatoire côté front.
  return { ok: true, data: { requiresOtp: true, email } };
}

/** Fusionne `profile.*` dans l’objet user (ex. `nip` souvent dans `profile` uniquement). */
export function normalizeProfessionalUser(user: ProfessionalUser | null | undefined): ProfessionalUser | null {
  if (!user) return null;
  const profile = (user.profile ?? {}) as Record<string, unknown>;
  const nipRaw = user.nip ?? profile.nip;
  const nip = typeof nipRaw === "string" ? nipRaw.trim() : undefined;
  return {
    ...user,
    nip: nip || undefined,
    nom: typeof user.nom === "string" ? user.nom : (profile.nom as string | undefined),
    prenom: typeof user.prenom === "string" ? user.prenom : (profile.prenom as string | undefined),
    specialite:
      typeof user.specialite === "string" ? user.specialite : (profile.specialite as string | undefined),
  };
}

function extractTokenAndUser(body: unknown): { token?: string; user?: ProfessionalUser } {
  let token: string | undefined;
  let user: ProfessionalUser | undefined;

  const visit = (node: unknown): void => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return;
    const obj = node as Record<string, unknown>;
    const t = obj.accessToken ?? obj.token;
    if (typeof t === "string" && t.trim() && !token) token = t.trim();
    const candidateUser = obj.user;
    if (candidateUser && typeof candidateUser === "object" && !Array.isArray(candidateUser) && !user) {
      user = normalizeProfessionalUser(candidateUser as ProfessionalUser) ?? (candidateUser as ProfessionalUser);
    }
    if (obj.data != null) visit(obj.data);
  };

  visit(body);
  return { token, user };
}

export async function verifyOtp(email: string, code: string): Promise<VerifyOtpResult> {
  const out = await apiFetch<unknown>(BASE + "/verify-otp", {
    method: "POST",
    // Le backend professionnel attend uniquement otpCode
    body: JSON.stringify({ otpCode: code }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  const { token, user } = extractTokenAndUser(out.data);
  if (token) setStoredToken(token);
  if (token && user) return { ok: true, data: { token, user } };
  return { ok: true };
}

type RefreshSuccessBody = {
  data?: { token?: string; accessToken?: string };
  token?: string;
  accessToken?: string;
};

export async function refreshToken(): Promise<{ ok: true; token: string } | { ok: false }> {
  const current = getStoredToken();
  const out = await apiFetch<RefreshSuccessBody>(BASE + "/refresh-token", {
    method: "POST",
    skipAuth: true,
    body: current ? JSON.stringify({ token: current }) : undefined,
  });
  if (!out.ok) return { ok: false };
  const { token } = extractTokenAndUser(out.data);
  if (typeof token === "string" && token.trim()) {
    setStoredToken(token);
    return { ok: true, token };
  }
  setStoredToken(null);
  return { ok: false };
}

export async function logout(): Promise<void> {
  await apiFetch(BASE + "/logout", { method: "POST", silent401: true });
  setStoredToken(null);
}

export async function getProfile(options?: {
  silent401?: boolean;
}): Promise<{ ok: true; data: ProfileResponse } | { ok: false; status: number; message: string }> {
  const out = await apiFetch<ProfileResponse>(BASE + "/profile", {
    silent401: options?.silent401,
  });
  if (!out.ok) return { ok: false, status: out.status, message: out.message };
  const payload = out.data as unknown as { data?: unknown } & Record<string, unknown>;
  const user = (payload?.data ?? payload) as ProfessionalUser;
  return { ok: true, data: normalizeProfessionalUser(user) ?? user };
}

export async function resetPassword(email: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(BASE + "/reset-password", {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function confirmPasswordReset(
  body: ConfirmPasswordResetBody
): Promise<{ ok: true } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(BASE + "/confirm-password-reset", {
    method: "POST",
    body: JSON.stringify(body),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function signup(
  body: SignupBody
): Promise<{ ok: true; data?: { user?: ProfessionalUser } } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(BASE + "/signup", {
    method: "POST",
    body: JSON.stringify(body),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as Record<string, unknown>;
  const inner = (payload?.data ?? payload) as Record<string, unknown>;
  const rawUser = inner?.user ?? payload?.user;
  const user =
    rawUser && typeof rawUser === "object" && !Array.isArray(rawUser)
      ? normalizeProfessionalUser(rawUser as ProfessionalUser) ?? undefined
      : undefined;
  return { ok: true, data: { user } };
}

export async function getSignupInfo(): Promise<
  { ok: true; data: SignupInfoResponse } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(BASE + "/signup-info", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: SignupInfoResponse } & SignupInfoResponse;
  const info = (payload?.data ?? payload) as SignupInfoResponse;
  return { ok: true, data: info };
}

export type SpecialiteOption = { code?: string; libelle?: string; label?: string; value?: string };

/** Réponse backend : tableau de chaînes ou d’objets selon version API. */
export async function getSpecialites(): Promise<{ ok: true; data: SpecialiteOption[] } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(BASE + "/specialites", { skipAuth: true });
  if (!out.ok) return { ok: false, message: out.message };
  const payload = out.data as { data?: unknown } & Record<string, unknown>;
  const raw = payload?.data ?? out.data;
  if (!Array.isArray(raw)) return { ok: true, data: [] };
  const data: SpecialiteOption[] = raw.map((item) => {
    if (typeof item === "string") return { libelle: item, label: item, value: item };
    if (item && typeof item === "object" && !Array.isArray(item)) return item as SpecialiteOption;
    return {};
  });
  return { ok: true, data };
}

export async function validateOrdre(
  numeroOrdre: string
): Promise<{ ok: true; valid: boolean } | { ok: false; message: string }> {
  const out = await apiFetch<{ valid?: boolean }>(BASE + "/validate-ordre", {
    method: "POST",
    body: JSON.stringify({ numero_ordre: numeroOrdre }),
    skipAuth: true,
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, valid: (out.data as { valid?: boolean }).valid ?? false };
}
